// THE FINGERPRINT THAT DECIDES WHETHER AN ENGINE NUMBER IS STILL TRUE.
//
// A line's engine price is only valid for the inputs it was computed from.
// This builds a string of exactly those inputs, so two things can be
// compared: what the columns are currently showing prices FOR, and what
// they were priced FROM. When they differ, the numbers are stale and get
// dropped.
//
// Lifted out of QuoteDetail.tsx when the reopen path needed the same
// fingerprint. ONE definition on purpose -- two copies would drift, and a
// drifted staleness check reads as "the numbers are fine" while they are
// not, which is the whole failure this guards against.
//
// The mode is part of the signature because the two chains take different
// inputs and produce different columns: an import price is not a stale
// domestic price, it is an answer to a different question.

import type { Material } from "@/types";

export type PricingMode = "domestic" | "import";

/** The pricing inputs of one line, in signature order.
 *
 *  shipmentKey is here because assigning a line to a shipment CHANGES WHAT
 *  IT WILL BE PRICED WITH: the allocator divides that shipment's bill across
 *  everything on it, so the line's freight comes from the assignment rather
 *  than from its own percentage. Re-assigning without invalidating would
 *  leave engine numbers on screen that were computed for a different
 *  shipment -- a stale validated number, which is the one thing this
 *  fingerprint exists to catch.
 *
 *  "" for unassigned, so an absent key and an empty one cannot produce two
 *  different fingerprints for the same line. */
function partsOf(
  lineNo: number,
  qty: number,
  netPrice: number,
  freightPct: number,
  conversionRate: number,
  dutyPct: number,
  shipmentKey: string,
): string {
  return [
    lineNo, qty, netPrice, freightPct, conversionRate, dutyPct, shipmentKey,
  ].join(":");
}

/** The fingerprint of what the table is currently showing. */
export function pricingSignatureOf(rows: Material[], mode: PricingMode): string {
  return `${mode}|` + rows
    .map((r) => partsOf(
      r.lineNo, r.qty, r.netPrice,
      r.freightPct ?? 0, r.conversionRate ?? 0, r.dutyPct ?? 0,
      r.shipmentKey ?? "",
    ))
    .join("|");
}

/** The fingerprint of what the BACKEND says it priced, from its own echoed
 *  inputs rather than from anything this app derived.
 *
 *  This is the half that makes the comparison worth doing. Both sides
 *  ultimately come from one response, but they arrive by different routes:
 *  this one reads the wire values directly, while pricingSignatureOf reads
 *  rows that have been through savedQuoteToMaterials and recomputeAll. That
 *  middle stretch is exactly where a mapping bug would land -- and it has
 *  twice -- so a disagreement here means the rows on screen are not the rows
 *  the stored prices belong to. */
export function pricingSignatureOfSavedLines(
  lines: Array<{
    qty: number;
    unit_price: number;
    freight_pct?: number;
    conversion_rate?: number;
    duty_pct?: number;
    shipment_id?: number | null;
  }>,
  mode: PricingMode,
  // Which shipment id maps to which client key, for THIS quote.
  //
  // Passed in rather than derived here, for two reasons. The mapping lives in
  // services/api.ts (shipmentKeyForId / resolveLineShipmentKey) and importing
  // that module from this one would close a cycle -- api.ts imports this file.
  // And re-deriving "ship-" + id here would put the key format in a second
  // place, which is how the two sides of a comparison start disagreeing about
  // what they are comparing.
  //
  // ABSENT means "this quote has no shipments", which fingerprints every line
  // as unassigned -- exactly what the rows side produces for the same quote.
  shipmentKeyById?: Map<number, string>,
): string {
  return `${mode}|` + lines
    .map((line, index) => partsOf(
      // Line numbers are positional on reopen: the backend has ids, the
      // table has 1..n. savedQuoteToMaterials numbers them the same way.
      index + 1,
      line.qty,
      // unit_price IS netPrice for a reopened line -- it is what this app
      // sent as the buy price, and no discount split was ever stored.
      line.unit_price,
      line.freight_pct ?? 0,
      line.conversion_rate ?? 0,
      line.duty_pct ?? 0,
      // Guarded through the SAME map the rows side was built from, so a
      // shipment_id naming a shipment this quote did not return fingerprints
      // as unassigned on both sides rather than only on one.
      (line.shipment_id != null && shipmentKeyById?.get(line.shipment_id)) || "",
    ))
    .join("|");
}
